import { Tooltip } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { dp, margin, responsiveCSS } from "ds_legacy/materials/metrics";
import { Body, BodyProps } from "ds_legacy/materials/typography";
import { useHasOverFlown } from "dsl/hooks";
import React, { CSSProperties, forwardRef } from "react";
import styled from "styled-components";
import { CUSTOM_BLACK } from "./colors";

type BaseTypes = {
  aligned?: boolean;
  backgroundColor?: string;
  border?: string;
  borderRadius?: string;
  boxShadow?: string;
  boxSizing?: CSSProperties["boxSizing"];
  cursor?: string;
  flexBasis?: string;
  flexShrink?: number;
  flexWrap?: string;
  gap?: CSSProperties["gap"];
  height?: string;
  justify?: CSSProperties["justifyContent"];
  margin?: string;
  maxWidth?: CSSProperties["maxWidth"];
  minHeight?: string;
  minWidth?: string;
  overflow?: string;
  padding?: CSSProperties["padding"];
  rowGap?: CSSProperties["rowGap"];
  stretched?: boolean;
  visibility?: CSSProperties["visibility"];
  width?: CSSProperties["width"];
  zIndex?: number | string;
};

type LongBodyProps = BodyProps & {
  children: React.ReactNode | null | undefined;
  style?: React.CSSProperties;
};

const BaseLayout = styled.div<BaseTypes>`
  display: flex;
  flex-shrink: ${(props) => props.flexShrink || "0"};
  flex-wrap: ${(props) => props.flexWrap || "initial"};
  margin: ${(props) => props.margin || "0"};
  padding: ${(props) => props.padding || "0"};
  z-index: ${(props) => props.zIndex || undefined};
  overflow: ${(props) => props.overflow};
  visibility: ${(props) => props.visibility || undefined};
  cursor: ${(props) => props.cursor || undefined};
  box-shadow: ${(props) => props.boxShadow || "none"};
  border: ${(props) => props.border || dp(0)};
  border-radius: ${(props) => props.borderRadius || dp(0)};
  height: ${(props) => props.height};
  width: ${(props) => props.width};
  min-height: ${(props) => props.minHeight};
  min-width: ${(props) => props.minWidth};
  max-width: ${(props) => props.maxWidth};
  background-color: ${(props) => props.backgroundColor};
  flex-basis: ${(props) => props.flexBasis || "auto"};
  gap: ${({ gap }) => gap || 0};
  row-gap: ${({ rowGap }) => rowGap};
  box-sizing: ${({ boxSizing }) => boxSizing || "border-box"};
  justify-content: ${(props) => (props.justify ? props.justify : undefined)};
  align-items: ${(props) => (props.aligned ? "center" : "stretch")};
  flex-grow: ${(props) => (props.stretched ? "1" : undefined)};
`;

export const HorizontalLayout = styled(BaseLayout)<BaseTypes>`
  flex-direction: row;
`;

export const VerticalLayout = styled(BaseLayout)<BaseTypes>`
  flex-direction: column;
`;

export const ResponsiveLayout = styled(BaseLayout)`
  flex-direction: column;
  @media print, (min-width: 600px) {
    & {
      flex-direction: row;
    }
  }
`;

export const HideOnMobile = styled.div<{
  andTablet?: boolean | null | undefined;
}>`
  ${({ andTablet }) =>
    responsiveCSS({
      mobile: [`display: none`],
      tablet: andTablet ? [`display: none`] : [],
    })}
`;

export const Divider = styled.hr<{
  height?: string;
  margin?: string;
  width?: string;
}>`
  border: ${dp(0)};
  height: ${(props) => props.height || dp(1)};
  opacity: 0.12;
  background-color: ${CUSTOM_BLACK};
  margin: ${(props) => props.margin || "0"};
  padding: 0;
  width: ${({ width }) => width};
`;

export const MultilineTextTruncation = styled(Body).withConfig({
  shouldForwardProp: (prop) => prop !== "lineCramp",
})<{
  lineCramp?: number;
}>`
  width: 100%;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${({ lineCramp }) => lineCramp || 1};
  overflow: hidden;
  display: -webkit-box;
`;

export const LongBody = forwardRef<HTMLParagraphElement, LongBodyProps>(
  ({ children, ...props }, ref) => {
    return (
      <Body
        ref={ref}
        as="p"
        margin={margin(0)}
        maxWidth="100%"
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
        {...props}
      >
        {children}
      </Body>
    );
  },
);

export function LongBodyTextWithTooltip({
  value,
  ...props
}: Omit<LongBodyProps, "children"> & { value: string }) {
  const { hasOverFlown, ref } = useHasOverFlown<HTMLParagraphElement>({
    direction: "x",
    value,
  });

  if (hasOverFlown) {
    return (
      <Tooltip title={value}>
        <LongBody ref={ref} {...props} aria-label={value}>
          {value}
        </LongBody>
      </Tooltip>
    );
  }

  return (
    <LongBody ref={ref} {...props}>
      {value}
    </LongBody>
  );
}

export const Spacer = styled.div<{
  height?: string;
  margin?: string;
  padding?: string;
}>`
  display: flex;
  ${({ height }) => (height ? `height: ${height}` : "flex: 1")};
  margin: ${({ margin }) => margin ?? 0};
  padding: ${({ padding }) => padding ?? 0};
`;

export const MarginAdjuster = styled.div<{
  display?: string;
  margin?: string | false;
}>`
  margin: ${(props) => props.margin};
  display: ${(props) => props.display};
`;

export const iconStyle = {
  fontSize: dp(20),
  margin: margin(0, 1, 0, 0),
};

export const iconHoverBorderStyle = (theme: Theme) => ({
  backgroundColor: "inherit",
  outline: `${dp(1)} solid ${theme.palette.primary.main}`,
});

export const ShowElement = styled.div<{
  show: boolean;
}>`
  display: ${({ show }: { show: boolean }) => (show ? "inline-block" : "none")};
`;

export const HorizontalDivider = styled.div<{
  color: CSSProperties["color"];
}>`
  width: 100%;
  height: ${dp(1)};
  margin: ${margin(1, 0)};
  background: ${({ color }) => color};
`;

export const ZeroSpacingList = styled.ul`
  margin: 0;
  padding: 0;
`;
